import React, { FunctionComponent, useEffect, useState } from 'react';

import classnames from 'classnames';
import { isFunction, omit } from 'lodash';

import { RatingInputs } from 'core/rating/rating-inputs/RatingInputs';
import { RatingScale } from 'core/rating/rating-scale/RatingScale';
import { RatingValue } from 'core/rating/rating-value/RatingValue';
import { RatingProps } from 'core/rating/types';
import {
    DEFAULT_COLOR_EMPTY,
    DEFAULT_COLOR_FULL,
    getColorByValueMap,
    parseColorEmpty,
    parseColorFull,
    parseIncrement,
    parseSize,
    parseValueMax,
    parseValueMin,
} from 'core/rating/util/Props';
import { getPotentialIncrementedValues, getPotentialIntegerValues, isUnrated } from 'core/rating/util/Values';

import 'core/rating/Rating.scss';

export const Rating: FunctionComponent<RatingProps> = ({
    className,
    colorEmpty: colorEmptyFromProps,
    colorFull: colorFullFromProps,
    htmlName,
    iconEmpty,
    iconFull,
    increment: incrementFromProps,
    isDisplayIcons,
    isDisplayScale,
    isDisplayScaleValue,
    isDisplayStacked,
    isDisplayValue,
    isReadOnly,
    onChange: onChangeFromProps,
    size: sizeFromProps,
    tabIndex,
    selectedValue: selectedValueFromProps,
    sentimentNegativeImage,
    sentimentNegativeLabel,
    sentimentNeutralImage,
    sentimentNeutralLabel,
    sentimentPositiveImage,
    sentimentPositiveLabel,
    valueMax: valueMaxFromProps,
    valueMin: valueMinFromProps,
    label,
    noRatingText,
    numberFormat,
}) => {
    const [selectedValue, setSelectedValue] = useState<number | undefined | null>(selectedValueFromProps);
    const colorEmpty = parseColorEmpty(colorEmptyFromProps);
    const colorFull = parseColorFull(colorFullFromProps);
    const increment = parseIncrement(incrementFromProps);
    const valueMax = parseValueMax(valueMaxFromProps);
    const valueMin = parseValueMin(valueMinFromProps);
    const size = parseSize(sizeFromProps);
    const potentialValues = getPotentialIntegerValues(valueMin, valueMax);
    const isNoRating = isUnrated(selectedValue);
    const onChange = (value?: number): void => {
        setSelectedValue(value);

        if (isFunction(onChangeFromProps)) {
            onChangeFromProps(value);
        }
    };
    let potentialValuesWithIncrements: number[] = [];

    useEffect(() => {
        if (isReadOnly && selectedValue !== selectedValueFromProps) {
            setSelectedValue(selectedValueFromProps);
        }
    }, [isReadOnly, selectedValue, selectedValueFromProps, setSelectedValue]);

    potentialValues.forEach((potentialValue) => {
        const incrementedValues = getPotentialIncrementedValues(potentialValue, increment);

        potentialValuesWithIncrements = potentialValuesWithIncrements.concat(incrementedValues);
    });

    const colorEmptyByValue = getColorByValueMap(potentialValuesWithIncrements, colorEmpty);
    const colorFullByValue = getColorByValueMap(potentialValuesWithIncrements, colorFull);

    return (
        <div className={classnames('r4e-rating-container', className)}>
            {label && <div className="rating-label text-muted">{label}</div>}
            <div
                className={classnames(`r4e-rating size-${size}`, {
                    'state-read-only': isReadOnly,
                    'display-scale': isDisplayScale,
                    'display-stacked': isDisplayStacked,
                    'display-flex': !isDisplayStacked,
                })}
            >
                <div className="rating-controls">
                    <div className="rating-controls-content">
                        {isNoRating && noRatingText && (
                            <div className="rating-no-rating text-muted">{noRatingText}</div>
                        )}
                        {(!isReadOnly || isDisplayIcons) && (!isNoRating || (isNoRating && !noRatingText)) && (
                            <RatingInputs
                                colorEmptyByValue={colorEmptyByValue}
                                colorFullByValue={colorFullByValue}
                                htmlName={htmlName}
                                iconEmpty={iconEmpty}
                                iconFull={iconFull}
                                increment={increment}
                                isReadOnly={isReadOnly}
                                onChange={onChange}
                                potentialValues={potentialValues}
                                selectedValue={selectedValue}
                                size={size}
                                tabIndex={tabIndex}
                            />
                        )}
                        {isDisplayScale && (
                            <RatingScale
                                isDisplayScaleValue={isDisplayScaleValue}
                                selectedValue={selectedValue}
                                sentimentNegativeImage={sentimentNegativeImage}
                                sentimentNegativeLabel={sentimentNegativeLabel}
                                sentimentNeutralImage={sentimentNeutralImage}
                                sentimentNeutralLabel={sentimentNeutralLabel}
                                sentimentPositiveImage={sentimentPositiveImage}
                                sentimentPositiveLabel={sentimentPositiveLabel}
                                size={size}
                            />
                        )}
                    </div>
                </div>
                {isDisplayValue && (!isNoRating || (isNoRating && !noRatingText)) && (
                    <RatingValue
                        colorFull={isNoRating ? undefined : colorFullByValue[selectedValue as number]}
                        selectedValue={selectedValue}
                        size={size}
                        valueMax={valueMax}
                        numberFormat={numberFormat}
                    />
                )}
            </div>
        </div>
    );
};

Rating.defaultProps = omit(
    {
        ...RatingInputs.defaultProps,
        ...RatingScale.defaultProps,
        ...RatingValue.defaultProps,
        colorEmpty: DEFAULT_COLOR_EMPTY,
        colorFull: DEFAULT_COLOR_FULL,
        onChange: undefined,
        isDisplayScale: true,
        isDisplayStacked: false,
        isDisplayValue: true,
        isDisplayIcons: true,
        selectedValue: undefined,
        sentimentNegativeImage: undefined,
        sentimentNegativeLabel: undefined,
        sentimentNeutralImage: undefined,
        sentimentNeutralLabel: undefined,
        sentimentPositiveImage: undefined,
        sentimentPositiveLabel: undefined,
        size: parseSize(),
    },
    'colorEmptyByValue',
    'colorFullByValue',
    'potentialValues',
);

export { RatingInputs } from 'core/rating/rating-inputs/RatingInputs';

export { RatingScale } from 'core/rating/rating-scale/RatingScale';

export { Increment, Size } from 'core/rating/types';
